import { BooleanInput } from '@angular/cdk/coercion';
import { NgClass } from '@angular/common';
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    Input,
    OnDestroy,
    OnInit,
    ViewEncapsulation,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { Router } from '@angular/router';
import { UserService } from 'app/core/user/user.service';
import { User } from 'app/core/user/user.types';
import { Subject, takeUntil } from 'rxjs';
import { FeaturebaseService } from 'app/core/featurebase.service';

@Component({
    selector: 'user',
    templateUrl: './user.component.html',
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    exportAs: 'user',
    imports: [
        MatButtonModule,
        MatMenuModule,
        MatIconModule,
        NgClass,
        MatDividerModule,
    ],
})
export class UserComponent implements OnInit, OnDestroy {
    /* eslint-disable @typescript-eslint/naming-convention */
    static ngAcceptInputType_showAvatar: BooleanInput;
    /* eslint-enable @typescript-eslint/naming-convention */

    @Input() showAvatar: boolean = true;
    user: User;

    private _unsubscribeAll: Subject<any> = new Subject<any>();

    /**
     * Constructor
     */
    constructor(
        private _changeDetectorRef: ChangeDetectorRef,
        private _router: Router,
        private _userService: UserService,
        private featurebase: FeaturebaseService
    ) {}

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    /**
     * On init
     */
    ngOnInit(): void {
        // Subscribe to user changes
        this._userService.user$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((user: User) => {
                this.user = user;

                // Initialize Featurebase widget with user email
                if (user && user.email) {
                    this.featurebase.initialize(user.email);
                }

                // Mark for check
                this._changeDetectorRef.markForCheck();
            });
    }

    /**
     * On destroy
     */
    ngOnDestroy(): void {
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Update the user status
     *
     * @param status
     */
    updateUserStatus(status: string): void {
        // Return if user is not available
        if (!this.user) {
            return;
        }

        // Update the user
        this._userService
            .update({
                ...this.user,
                status,
            })
            .subscribe();
    }

    /**
     * Sign out
     */
    signOut(): void {
        this._router.navigate(['/sign-out']);
    }

    /**
     * Navigate to user settings
     */
    navigateToUserSettings(): void {
        this._router.navigate(['/user/settings']);
    }

    /**
     * Navigate to user profile
     */
    navigateToUserProfile(): void {
        const currentUrl = this._router.url;
        // check if currentURL is deeper than 2 levels and
        // if so, go back to the previous level before going to /user/settings/profile
        let urlParts = currentUrl.split('/');
        // remove the empaty string from the array
        urlParts = urlParts.filter(part => part !== '');
        if (urlParts.length > 2) {
            // go back to the previous level
            this._router.navigate([urlParts[0], urlParts[1]]).then(() => {
                this._router.navigate(['/user/settings/profile']);
            });
            
        } else {
            this._router.navigate(['/user/settings/profile']);
        }
    }

    /**
     * Navigate to user preferences
     */
    navigateToUserPreferences(): void {
        const currentUrl = this._router.url;
        // check if currentURL is deeper than 2 levels and
        // if so, go back to the previous level before going to /user/settings/profile
        let urlParts = currentUrl.split('/');
        // remove the empaty string from the array
        urlParts = urlParts.filter(part => part !== '');
        if (urlParts.length > 2) {
            // go back to the previous level
            this._router.navigate([urlParts[0], urlParts[1]]).then(() => {
                this._router.navigate(['/user/settings/preferences']);
            });
            
        } else {
            this._router.navigate(['/user/settings/preferences']);
        }
       
    }
}
